import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import { uploadViaSignedUrl, type UploadProgressHandler } from './fileUpload';
import type { Label, Pagination } from './tasksApi';

// ---------------------------------------------------------------------------
// Leads — pipeline & stage configuration API client (CLE-193).
//
// Mirrors backend/src/routes/leads.ts (mounted at /api/leads):
//   GET    /api/leads/pipelines                       — list pipelines + stages
//   POST   /api/leads/pipelines                       — create pipeline (+stages)
//   GET    /api/leads/pipelines/:id                   — one pipeline
//   PUT    /api/leads/pipelines/:id                   — update name/desc/default
//   DELETE /api/leads/pipelines/:id                   — delete (blocked if leads)
//   POST   /api/leads/pipelines/:id/stages            — add stage
//   PUT    /api/leads/stages/:stageId                 — update stage
//   DELETE /api/leads/stages/:stageId                 — delete stage (blocked)
//   POST   /api/leads/pipelines/:id/stages/reorder    — reorder stages
//
// Backend enforces the invariant: each pipeline keeps ≥1 ACTIVE and ≥1 WON
// stage. The frontend enforces the same client-side before issuing requests.
// ---------------------------------------------------------------------------

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export type LeadStageType = 'ACTIVE' | 'WON' | 'LOST' | 'ARCHIVED';

export const LEAD_STAGE_TYPES: LeadStageType[] = ['ACTIVE', 'WON', 'LOST', 'ARCHIVED'];

export interface LeadStage {
  id: string;
  pipelineId: string;
  name: string;
  color: string;
  order: number;
  type: LeadStageType;
  createdAt: string;
}

/**
 * What a pipeline is for: a lead won in a SALES pipeline becomes a client and
 * moves into the ONBOARDING pipeline automatically (backend services/leadWon.ts).
 */
export type LeadPipelineKind = 'SALES' | 'ONBOARDING';

export const LEAD_PIPELINE_KINDS: LeadPipelineKind[] = ['SALES', 'ONBOARDING'];

export interface LeadPipeline {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  kind: LeadPipelineKind;
  stages: LeadStage[];
  createdAt: string;
  updatedAt: string;
}

export interface CreatePipelineInput {
  name: string;
  description?: string | null;
  isDefault?: boolean;
  kind?: LeadPipelineKind;
  stages?: Array<{ name: string; color: string; type?: LeadStageType }>;
}

export interface UpdatePipelineInput {
  name?: string;
  description?: string | null;
  isDefault?: boolean;
  kind?: LeadPipelineKind;
}

export interface CreateStageInput {
  name: string;
  color: string;
  type?: LeadStageType;
  order?: number;
}

export interface UpdateStageInput {
  name?: string;
  color?: string;
  type?: LeadStageType;
  order?: number;
}

// --- Pipelines -------------------------------------------------------------

export async function listPipelines(): Promise<LeadPipeline[]> {
  const res = await apiFetch<Envelope<{ pipelines: LeadPipeline[] }>>('/api/leads/pipelines', {
    headers: authHeaders(),
  });
  return res.data.pipelines;
}

export async function createPipeline(input: CreatePipelineInput): Promise<LeadPipeline> {
  const res = await apiFetch<Envelope<LeadPipeline>>('/api/leads/pipelines', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function updatePipeline(
  id: string,
  input: UpdatePipelineInput,
): Promise<LeadPipeline> {
  const res = await apiFetch<Envelope<LeadPipeline>>(`/api/leads/pipelines/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function deletePipeline(id: string): Promise<void> {
  await apiFetch<Envelope<{ deleted: true; id: string }>>(`/api/leads/pipelines/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
}

// --- Stages ----------------------------------------------------------------

export async function createStage(pipelineId: string, input: CreateStageInput): Promise<LeadStage> {
  const res = await apiFetch<Envelope<LeadStage>>(`/api/leads/pipelines/${pipelineId}/stages`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function updateStage(stageId: string, input: UpdateStageInput): Promise<LeadStage> {
  const res = await apiFetch<Envelope<LeadStage>>(`/api/leads/stages/${stageId}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function deleteStage(stageId: string): Promise<void> {
  await apiFetch<Envelope<{ deleted: true; id: string }>>(`/api/leads/stages/${stageId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
}

export async function reorderStages(pipelineId: string, stageIds: string[]): Promise<LeadStage[]> {
  const res = await apiFetch<Envelope<{ stages: LeadStage[] }>>(
    `/api/leads/pipelines/${pipelineId}/stages/reorder`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ stageIds }),
    },
  );
  return res.data.stages;
}

export async function getPipeline(id: string): Promise<LeadPipeline> {
  const res = await apiFetch<Envelope<LeadPipeline>>(`/api/leads/pipelines/${id}`, {
    headers: authHeaders(),
  });
  return res.data;
}

// ===========================================================================
// Leads — entity, board, stats, actions API client (CLE-191).
//
// Mirrors backend/src/routes/leads.ts:
//   GET    /api/leads                 — list (filters/search/sort/pagination)
//   POST   /api/leads                 — create
//   GET    /api/leads/:id             — one lead
//   PUT    /api/leads/:id             — update (logs activity)
//   DELETE /api/leads/:id             — archive (soft delete)
//   GET    /api/leads/board           — columns grouped by pipeline stage
//   GET    /api/leads/stats           — aggregates by stage
//   POST   /api/leads/bulk            — bulk stage/assign/temperature/archive
//   POST   /api/leads/:id/convert     — convert to project (moves to WON)
//   *      /api/leads/:id/actions     — per-lead actions CRUD
// ===========================================================================

export type LeadTemperature = 'HOT' | 'WARM' | 'COLD';
export type LeadSource =
  | 'MANUAL'
  | 'AI_ASSISTANT'
  | 'REFERRAL'
  | 'WEBSITE'
  | 'EMAIL'
  | 'PHONE'
  | 'SOCIAL_MEDIA'
  | 'EVENT'
  | 'OTHER';
export type LeadActionType =
  | 'CALL'
  | 'EMAIL'
  | 'MEETING'
  | 'TASK'
  | 'FOLLOW_UP'
  | 'PROPOSAL'
  | 'DEMO'
  | 'OTHER';

/**
 * Present on the response of the move that won a lead in a sales pipeline: it
 * became (or was linked to) a client and, usually, moved into onboarding.
 */
export interface LeadTransition {
  clientId: string;
  clientName: string;
  clientCreated: boolean;
  toPipeline: { id: string; name: string } | null;
}

export interface Lead {
  id: string;
  identifier: string;
  title: string;
  description: string | null;
  pipelineId: string;
  pipeline: { id: string; name: string; kind: LeadPipelineKind } | null;
  stageId: string;
  stage: {
    id: string;
    name: string;
    color: string;
    type: LeadStageType;
    order: number;
  } | null;
  stageOrder: number;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  companyName: string | null;
  value: number | null;
  currency: string;
  source: LeadSource;
  // Typed by the user ("LinkedIn Ads"); shown instead of the enum when set.
  sourceLabel: string | null;
  temperature: LeadTemperature;
  assigneeId: string | null;
  assignee: { id: string; name: string } | null;
  creatorId: string | null;
  creator: { id: string; name: string } | null;
  convertedToProjectId: string | null;
  convertedAt: string | null;
  clientId: string | null;
  expectedCloseDate: string | null;
  lastContactedAt: string | null;
  labels: Label[];
  actionCount: number;
  commentCount: number;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  /** Only on the response of the move that triggered the WON rule. */
  transition?: LeadTransition;
}

export interface LeadAction {
  id: string;
  leadId: string;
  type: LeadActionType;
  title: string;
  description: string | null;
  assigneeId: string | null;
  assignee: { id: string; name: string } | null;
  dueDate: string | null;
  completedAt: string | null;
  isCompleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LeadListResult {
  leads: Lead[];
  pagination: Pagination;
}

export interface LeadBoardColumn {
  stage: LeadStage;
  leads: Lead[];
  count: number;
  valueSum: number;
}

export interface LeadBoardResult {
  pipeline: LeadPipeline;
  columns: LeadBoardColumn[];
}

export interface LeadStats {
  totalLeads: number;
  totalValue: number;
  conversionRate: number;
  wonLeads: number;
  avgDaysInStage: number;
  byStage: Array<{
    stageId: string;
    stageName: string | null;
    stageType: LeadStageType | null;
    count: number;
    value: number;
  }>;
}

export interface LeadFilters {
  pipelineId?: string;
  pipelineKind?: LeadPipelineKind;
  clientId?: string;
  stageId?: string;
  assigneeId?: string | string[];
  temperature?: LeadTemperature | LeadTemperature[];
  source?: LeadSource | LeadSource[];
  // Sources typed by the user (Lead.sourceLabel).
  sourceLabel?: string | string[];
  search?: string;
  label?: string | string[];
  hasOverdueActions?: boolean;
  archived?: boolean;
  sort?: 'value' | 'temperature' | 'createdAt' | 'updatedAt' | 'expectedCloseDate';
  order?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

function leadFilterParams(filters: object): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === '' || value === false) continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      // Typed sources are free text and may contain commas: repeat the param.
      if (key === 'sourceLabel') for (const v of value) params.append(key, String(v));
      else params.set(key, value.join(','));
    } else {
      params.set(key, String(value));
    }
  }
  return params;
}

export async function listLeads(filters: LeadFilters = {}): Promise<LeadListResult> {
  const qs = leadFilterParams(filters).toString();
  const res = await apiFetch<Envelope<LeadListResult>>(`/api/leads${qs ? `?${qs}` : ''}`, {
    headers: authHeaders(),
  });
  return res.data;
}

/** BNR rates: lei per one unit of each currency (RON = 1). */
export interface FxRates {
  date: string;
  ronPer: Record<string, number>;
}

// Today's BNR rates; null when BNR could not be reached and nothing is cached.
export async function getLeadFxRates(): Promise<FxRates | null> {
  const res = await apiFetch<Envelope<{ rates: FxRates | null }>>('/api/leads/fx-rates', {
    headers: authHeaders(),
  });
  return res.data.rates;
}

export interface LeadCustomSource {
  label: string;
  count: number;
}

export interface LeadSourcesInUse {
  // Built-in sources on leads without a typed one.
  builtIn: Array<{ source: LeadSource; count: number }>;
  // Sources people typed themselves, most used first.
  sources: LeadCustomSource[];
}

export async function listLeadSourcesInUse(): Promise<LeadSourcesInUse> {
  const res = await apiFetch<Envelope<LeadSourcesInUse>>('/api/leads/sources', {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getLead(id: string): Promise<Lead> {
  const res = await apiFetch<Envelope<Lead>>(`/api/leads/${id}`, { headers: authHeaders() });
  return res.data;
}

export interface CreateLeadInput {
  title: string;
  description?: string | null;
  pipelineId?: string;
  stageId?: string;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  companyName?: string | null;
  value?: number | null;
  currency?: string;
  source?: LeadSource;
  sourceLabel?: string | null;
  temperature?: LeadTemperature;
  assigneeId?: string | null;
  clientId?: string | null;
  expectedCloseDate?: string | null;
  labelIds?: string[];
}

export async function createLead(input: CreateLeadInput): Promise<Lead> {
  const res = await apiFetch<Envelope<Lead>>('/api/leads', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export interface UpdateLeadInput {
  title?: string;
  description?: string | null;
  stageId?: string;
  stageOrder?: number;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  companyName?: string | null;
  value?: number | null;
  currency?: string;
  source?: LeadSource;
  sourceLabel?: string | null;
  temperature?: LeadTemperature;
  assigneeId?: string | null;
  clientId?: string | null;
  expectedCloseDate?: string | null;
  lastContactedAt?: string | null;
  labelIds?: string[];
}

export async function updateLead(id: string, input: UpdateLeadInput): Promise<Lead> {
  const res = await apiFetch<Envelope<Lead>>(`/api/leads/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function archiveLead(id: string): Promise<{ archived: boolean; id: string }> {
  const res = await apiFetch<Envelope<{ archived: boolean; id: string }>>(`/api/leads/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return res.data;
}

export async function restoreLead(id: string): Promise<Lead> {
  const res = await apiFetch<Envelope<Lead>>(`/api/leads/${id}/restore`, {
    method: 'POST',
    headers: authHeaders(),
  });
  return res.data;
}

export interface ConvertLeadInput {
  projectName?: string;
  projectCode?: string;
  ownerId?: string | null;
}

export async function convertLead(id: string, input: ConvertLeadInput = {}): Promise<Lead> {
  const res = await apiFetch<Envelope<Lead>>(`/api/leads/${id}/convert`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

// Board filters mirror the list ones (minus pagination/sorting) so the search
// box and the filter row apply to the Kanban as well.
export type LeadBoardFilters = Pick<
  LeadFilters,
  | 'pipelineId'
  | 'assigneeId'
  | 'temperature'
  | 'source'
  | 'sourceLabel'
  | 'search'
  | 'label'
  | 'hasOverdueActions'
>;

export async function getBoard(filters: LeadBoardFilters = {}): Promise<LeadBoardResult> {
  const qs = leadFilterParams(filters).toString();
  const res = await apiFetch<Envelope<LeadBoardResult>>(`/api/leads/board${qs ? `?${qs}` : ''}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getStats(pipelineId?: string): Promise<LeadStats> {
  const qs = pipelineId ? `?pipelineId=${pipelineId}` : '';
  const res = await apiFetch<Envelope<LeadStats>>(`/api/leads/stats${qs}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export type LeadBulkAction = 'stage' | 'assign' | 'temperature' | 'archive';

export interface LeadBulkResult {
  affected: number;
  /** Leads the stage action won (WON rule), one entry each. */
  transitions?: Array<LeadTransition & { leadId: string }>;
}

export async function bulkUpdateLeads(
  ids: string[],
  action: LeadBulkAction,
  value?: unknown,
): Promise<LeadBulkResult> {
  const res = await apiFetch<Envelope<LeadBulkResult>>('/api/leads/bulk', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ ids, action, value }),
  });
  return res.data;
}

// --- Actions ---------------------------------------------------------------

export async function listActions(leadId: string): Promise<LeadAction[]> {
  const res = await apiFetch<Envelope<{ actions: LeadAction[] }>>(`/api/leads/${leadId}/actions`, {
    headers: authHeaders(),
  });
  return res.data.actions;
}

// --- AI action suggestions (same flow as a meeting's task suggestions) ---------

export type LeadSuggestionStatus = 'PENDING' | 'ACCEPTED' | 'DISMISSED';
export type LeadSuggestionPriority = 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

export interface LeadTaskSuggestion {
  id: string;
  leadId: string | null;
  noteId: string | null;
  title: string;
  description: string | null;
  reason: string | null;
  priority: LeadSuggestionPriority;
  dueDate: string | null;
  actionType: LeadActionType | null;
  assignee: { id: string; name: string } | null;
  assigneeHint: string | null;
  status: LeadSuggestionStatus;
  leadAction: { id: string; title: string; type: LeadActionType; isCompleted: boolean } | null;
  decidedBy: { id: string; name: string } | null;
  decidedAt: string | null;
  position: number;
  createdAt: string;
  updatedAt: string;
}

export interface AcceptLeadSuggestionInput {
  title?: string;
  description?: string | null;
  type?: LeadActionType;
  assigneeId?: string | null;
  dueDate?: string | null;
}

function suggestionsUrl(leadId: string, tail = ''): string {
  return `/api/leads/${encodeURIComponent(leadId)}/task-suggestions${tail}`;
}

export async function listLeadSuggestions(leadId: string): Promise<LeadTaskSuggestion[]> {
  const res = await apiFetch<Envelope<LeadTaskSuggestion[]>>(suggestionsUrl(leadId), {
    headers: authHeaders(),
  });
  return Array.isArray(res.data) ? res.data : [];
}

export async function generateLeadSuggestions(leadId: string): Promise<LeadTaskSuggestion[]> {
  const res = await apiFetch<Envelope<LeadTaskSuggestion[]>>(suggestionsUrl(leadId, '/generate'), {
    method: 'POST',
    headers: authHeaders(),
    body: '{}',
  });
  return Array.isArray(res.data) ? res.data : [];
}

export async function acceptLeadSuggestion(
  leadId: string,
  id: string,
  input: AcceptLeadSuggestionInput,
): Promise<LeadTaskSuggestion> {
  const res = await apiFetch<Envelope<LeadTaskSuggestion>>(
    suggestionsUrl(leadId, `/${encodeURIComponent(id)}/accept`),
    { method: 'POST', headers: authHeaders(), body: JSON.stringify(input) },
  );
  return res.data;
}

export async function dismissLeadSuggestion(
  leadId: string,
  id: string,
): Promise<LeadTaskSuggestion> {
  const res = await apiFetch<Envelope<LeadTaskSuggestion>>(
    suggestionsUrl(leadId, `/${encodeURIComponent(id)}/dismiss`),
    { method: 'POST', headers: authHeaders(), body: '{}' },
  );
  return res.data;
}

export async function restoreLeadSuggestion(
  leadId: string,
  id: string,
): Promise<LeadTaskSuggestion> {
  const res = await apiFetch<Envelope<LeadTaskSuggestion>>(
    suggestionsUrl(leadId, `/${encodeURIComponent(id)}/restore`),
    { method: 'POST', headers: authHeaders(), body: '{}' },
  );
  return res.data;
}

export interface CreateActionInput {
  type: LeadActionType;
  title: string;
  description?: string | null;
  assigneeId?: string | null;
  dueDate?: string | null;
}

export async function createAction(leadId: string, input: CreateActionInput): Promise<LeadAction> {
  const res = await apiFetch<Envelope<LeadAction>>(`/api/leads/${leadId}/actions`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export interface UpdateActionInput {
  type?: LeadActionType;
  title?: string;
  description?: string | null;
  assigneeId?: string | null;
  dueDate?: string | null;
  isCompleted?: boolean;
}

export async function updateAction(
  leadId: string,
  actionId: string,
  input: UpdateActionInput,
): Promise<LeadAction> {
  const res = await apiFetch<Envelope<LeadAction>>(`/api/leads/${leadId}/actions/${actionId}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function deleteAction(
  leadId: string,
  actionId: string,
): Promise<{ deleted: boolean; id: string }> {
  const res = await apiFetch<Envelope<{ deleted: boolean; id: string }>>(
    `/api/leads/${leadId}/actions/${actionId}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  return res.data;
}

export async function listOverdueActions(assigneeId?: string): Promise<LeadAction[]> {
  const qs = assigneeId ? `?assigneeId=${assigneeId}` : '';
  const res = await apiFetch<Envelope<{ actions: LeadAction[] }>>(
    `/api/leads/actions/overdue${qs}`,
    { headers: authHeaders() },
  );
  return res.data.actions;
}

// ===========================================================================
// Leads — comments, activity & unified timeline API client (CLE-192).
//
// Mirrors backend/src/routes/leads.ts:
//   GET    /api/leads/:id/comments               — chronological comments
//   POST   /api/leads/:id/comments               — add comment (@mentions)
//   PATCH  /api/leads/:leadId/comments/:id        — author-only edit
//   DELETE /api/leads/:leadId/comments/:id        — delete comment
//   POST   /api/leads/:id/upload-url             — signed attachment upload URL
//   GET    /api/leads/:id/activity               — change history
//   GET    /api/leads/:id/timeline               — unified comments + activity
// ===========================================================================

export interface LeadCommentAttachment {
  url: string;
  name: string;
  contentType: string;
}

export interface LeadComment {
  id: string;
  leadId: string;
  authorId: string | null;
  author: { id: string; name: string } | null;
  content: string;
  attachments: LeadCommentAttachment[];
  createdAt: string;
  updatedAt: string;
}

export interface LeadActivity {
  id: string;
  leadId: string;
  userId: string | null;
  user: { id: string; name: string } | null;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  createdAt: string;
}

export type LeadTimelineItem =
  | { kind: 'comment'; at: string; data: LeadComment }
  | { kind: 'activity'; at: string; data: LeadActivity };

export async function getLeadTimeline(leadId: string): Promise<LeadTimelineItem[]> {
  const res = await apiFetch<Envelope<{ timeline: LeadTimelineItem[] }>>(
    `/api/leads/${leadId}/timeline`,
    { headers: authHeaders() },
  );
  return res.data.timeline;
}

export async function addLeadComment(
  leadId: string,
  content: string,
  attachments?: LeadCommentAttachment[],
): Promise<{ comment: LeadComment; mentioned: Array<{ id: string; name: string }> }> {
  const res = await apiFetch<
    Envelope<{ comment: LeadComment; mentioned: Array<{ id: string; name: string }> }>
  >(`/api/leads/${leadId}/comments`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      content,
      ...(attachments && attachments.length ? { attachments } : {}),
    }),
  });
  return res.data;
}

export async function updateLeadComment(
  leadId: string,
  commentId: string,
  input: { content?: string; attachments?: LeadCommentAttachment[] | null },
): Promise<LeadComment> {
  const res = await apiFetch<Envelope<{ comment: LeadComment }>>(
    `/api/leads/${leadId}/comments/${commentId}`,
    {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(input),
    },
  );
  return res.data.comment;
}

/** Remove one file from a lead comment; see `deleteCommentAttachment` in tasksApi. */
export async function deleteLeadCommentAttachment(
  leadId: string,
  commentId: string,
  index: number,
  name: string,
): Promise<{ deleted?: boolean; comment?: LeadComment }> {
  const res = await apiFetch<Envelope<{ deleted?: boolean; comment?: LeadComment }>>(
    `/api/leads/${leadId}/comments/${commentId}/attachments/${index}?name=${encodeURIComponent(name)}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  return res.data;
}

export async function deleteLeadComment(
  leadId: string,
  commentId: string,
): Promise<{ deleted: boolean; id: string }> {
  const res = await apiFetch<Envelope<{ deleted: boolean; id: string }>>(
    `/api/leads/${leadId}/comments/${commentId}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  return res.data;
}

interface LeadUploadUrlResult {
  uploadUrl: string;
  key: string;
  publicUrl: string;
  expiresIn: number;
}

async function requestLeadUploadUrl(
  leadId: string,
  filename: string,
  contentType: string,
): Promise<LeadUploadUrlResult> {
  const res = await apiFetch<Envelope<LeadUploadUrlResult>>(`/api/leads/${leadId}/upload-url`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ filename, contentType }),
  });
  return res.data;
}

/**
 * Upload a file for a lead: request a signed URL, PUT the bytes to storage, and
 * return the attachment to embed in markdown or attach to a comment (used by the
 * description editor and the comment composer).
 */
export async function uploadLeadFile(
  leadId: string,
  file: File,
  onProgress?: UploadProgressHandler,
): Promise<LeadCommentAttachment> {
  return uploadViaSignedUrl(
    file,
    (filename, contentType) => requestLeadUploadUrl(leadId, filename, contentType),
    onProgress,
  );
}
